import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { eur } from "@/lib/api";
import NotaryFeesBlock from "@/components/NotaryFeesBlock";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import AnimatedNumber from "@/components/AnimatedNumber";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Calculator } from "lucide-react";

const AGE_HYPOTHESE = 8;

function computeDecote(propertyType, years = AGE_HYPOTHESE) {
  if (propertyType !== "occupee") return 0;
  const raw = 0.05 + 0.015 * years;
  return Math.min(0.40, raw);
}

// Formatte un nombre avec des séparateurs de milliers (espace fine, fr-FR).
// On garantit que le séparateur reste une "espace fine insécable" cohérente.
const formatEurAmount = (n) => {
  const num = Math.round(n || 0);
  // Format 137500 -> "137 500" (avec espace fine U+202F ou fallback espace normale)
  return String(num).replace(/\B(?=(\d{3})+(?!\d))/g, "\u202F");
};
// Nettoie une saisie utilisateur en un entier positif (accepte espaces, points, virgules ignorés)
const parseEurInput = (raw) => {
  const digits = String(raw).replace(/[^\d]/g, "");
  if (digits === "") return { valid: false, num: 0, digits: "" };
  return { valid: true, num: parseInt(digits, 10), digits };
};
// Formatte un pourcentage avec au plus 1 décimale, sans zéro trailing inutile.
const formatPct = (p) => {
  const rounded = Math.round(p * 10) / 10;
  return Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1).replace(".", ",");
};

export default function Simulateur() {
  const [params] = useSearchParams();
  const [propertyType, setPropertyType] = useState(() => params.get("property_type") === "occupee" ? "occupee" : "libre");
  const [marketValue, setMarketValue] = useState(() => Math.max(50000, Number(params.get("market_value")) || 500000));
  // Bouquet en EUR = source de vérité. Init à 20 % de la valeur retenue par défaut.
  const initBouquet = Math.round(Math.max(50000, Number(params.get("market_value")) || 500000) * 0.20);
  const [bouquet, setBouquet] = useState(initBouquet);
  // Affichage libre du champ EUR (formaté hors édition, brut pendant édition).
  const [bouquetInput, setBouquetInput] = useState(formatEurAmount(initBouquet));
  const [editing, setEditing] = useState(false);
  const [duration, setDuration] = useState(15);
  const [duh, setDuh] = useState(8);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const mv = Number(params.get("market_value"));
    if (mv && mv >= 50000) {
      setMarketValue(mv);
      const nb = Math.round(mv * 0.20);
      setBouquet(nb);
      setBouquetInput(formatEurAmount(nb));
    }
    if (params.get("property_type") === "occupee") setPropertyType("occupee");
    else if (params.get("property_type") === "libre") setPropertyType("libre");
  }, [params]);

  const decote = useMemo(() => computeDecote(propertyType, duh), [propertyType, duh]);
  const retenueValue = useMemo(() => Math.round(marketValue * (1 - decote)), [marketValue, decote]);

  // Pourcentage indicatif calculé (arrondi à 1 décimale à l'affichage seulement)
  const bouquetPctRaw = retenueValue > 0 ? (bouquet / retenueValue) * 100 : 0;
  const bouquetPctDisplay = formatPct(bouquetPctRaw);

  const solde = useMemo(() => Math.max(0, retenueValue - bouquet), [retenueValue, bouquet]);
  const monthly = useMemo(() => Math.round(solde / (duration * 12)), [solde, duration]);

  // Handler slider — pilote le bouquet en % de la valeur retenue par pas de 1 %.
  const onSliderChange = (v) => {
    const pct = v[0];
    const newBouquet = Math.round((retenueValue * pct) / 100);
    setBouquet(newBouquet);
    if (!editing) setBouquetInput(formatEurAmount(newBouquet));
  };

  // Saisie libre : chiffres uniquement, sans contrainte de min/max.
  const onBouquetInput = (e) => {
    const parsed = parseEurInput(e.target.value);
    // Pendant l'édition, on garde la version brute lisible pour ne pas casser le curseur.
    setBouquetInput(parsed.digits);
    setBouquet(parsed.valid ? parsed.num : 0);
  };

  const onBouquetFocus = () => {
    setEditing(true);
    // Affiche le brut pour édition fluide
    setBouquetInput(String(bouquet));
  };
  const onBouquetBlur = () => {
    setEditing(false);
    setBouquetInput(formatEurAmount(bouquet));
  };

  // Valeur du slider (dérivée du bouquet EUR courant, clampée à [0, 90])
  const sliderValue = useMemo(() => {
    if (retenueValue <= 0) return 0;
    return Math.max(0, Math.min(90, Math.round((bouquet / retenueValue) * 100)));
  }, [bouquet, retenueValue]);

  // Recalage doux du bouquet si la valeur de marché change et que l'utilisateur n'édite pas.
  useEffect(() => {
    if (!editing) setBouquetInput(formatEurAmount(bouquet));
  }, [bouquet, editing]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/leads", {
        email, phone, name,
        market_value: marketValue, property_type: propertyType,
        bouquet_percent: Math.round(bouquetPctRaw * 10) / 10, duration_years: duration,
        bouquet, monthly_payment: monthly, source: "simulateur",
      });
      toast.success("Merci — un conseiller vous recontacte sous 24 h.");
      setName(""); setEmail(""); setPhone("");
    } catch { toast.error("Erreur d'envoi, réessayez."); }
    finally { setSubmitting(false); }
  };

  const downloadPdf = async () => {
    // Code-splitting : jsPDF chargé à la demande uniquement
    const { generateRecapPDF } = await import("@/lib/generatePdf");
    generateRecapPDF({
      context: "simulateur",
      propertyType,
      marketValue,
      retainedValue: retenueValue,
      bouquet,
      monthlyPayment: monthly,
      duration,
      notaryKind: "ancien",
    });
  };

  const bouquetDisplayValue = bouquetInput;

  return (
    <>
      <SEO title="Simulateur vente à terme — venteaterme.fr" description="Calculez instantanément votre bouquet, votre mensualité et la valeur retenue en vente à terme. Simulation gratuite, sans engagement." />

      <section className="pt-24 pb-12 bg-navy text-cream">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3 mb-4">
            <Calculator className="text-accent-brand" size={22} />
            <div className="text-xs uppercase tracking-[0.3em] text-accent-brand font-bold">Simulateur</div>
          </div>
          <h1 className="font-serif text-4xl md:text-6xl leading-tight text-balance">Calculez votre opération en temps réel.</h1>
          <p className="text-cream/80 mt-6 max-w-2xl">Ajustez les paramètres — bouquet, durée, type — et découvrez les montants exacts. Estimation indicative.</p>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-6 md:px-10 grid lg:grid-cols-[1.1fr_1fr] gap-8">
          {/* PARAMS */}
          <Reveal className="bg-white border border-navy/10 p-8 md:p-10">
            <h2 className="font-serif text-2xl text-navy mb-8">Paramètres</h2>

            <div className="mb-8">
              <Label className="text-[10px] uppercase tracking-wider text-navy/60 font-bold">Type de vente</Label>
              <RadioGroup value={propertyType} onValueChange={setPropertyType} className="grid grid-cols-2 gap-4 mt-3">
                {[["libre", "Libre"], ["occupee", "Occupée"]].map(([v, lb]) => (
                  <label key={v} data-testid={`sim-type-${v}`}
                    className={`cursor-pointer border p-4 flex items-center gap-3 transition-colors ${propertyType === v ? "border-accent-brand bg-accent-brand/5" : "border-navy/15"}`}>
                    <RadioGroupItem value={v} id={`t-${v}`} />
                    <span className="font-serif text-navy">{lb}</span>
                  </label>
                ))}
              </RadioGroup>
            </div>

            <div className="mb-8">
              <div className="flex items-baseline justify-between mb-3">
                <Label className="text-[10px] uppercase tracking-wider text-navy/60 font-bold">Valeur vénale du bien</Label>
                <span className="font-serif text-lg text-navy tabular-nums">{eur(marketValue)}</span>
              </div>
              <Input data-testid="sim-market-value" type="number" min={50000} step={10000} value={marketValue}
                onChange={(e) => setMarketValue(Math.max(50000, Number(e.target.value) || 0))} className="border-navy/20 tabular-nums" />
            </div>

            <div className="mb-8">
              <div className="flex items-baseline justify-between mb-3">
                <Label className="text-[10px] uppercase tracking-wider text-navy/60 font-bold">Bouquet initial</Label>
                <span className="font-serif text-lg text-navy tabular-nums">
                  {eur(bouquet)} <span className="text-navy/50 text-sm">— soit ~{bouquetPctDisplay}&nbsp;%</span>
                </span>
              </div>

              {/* Champ EUR = PRINCIPAL, saisie 100 % libre */}
              <div className="mb-3">
                <div className="text-[10px] uppercase tracking-wider text-navy/50 font-bold mb-1">Montant du bouquet (EUR)</div>
                <div className="flex items-center gap-1 border border-navy/20 focus-within:border-accent-brand bg-white">
                  <Input
                    data-testid="sim-bouquet-eur-input"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    value={bouquetDisplayValue}
                    onChange={onBouquetInput}
                    onFocus={onBouquetFocus}
                    onBlur={onBouquetBlur}
                    placeholder="Ex. 137 500"
                    className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 tabular-nums text-lg font-serif text-navy"
                  />
                  <span className="px-3 text-navy/60 font-mono">€</span>
                </div>
                <div className="text-[11px] text-navy/50 mt-1.5">Saisissez librement le montant souhaité. Le pourcentage ci-dessous est purement indicatif.</div>
              </div>

              {/* Slider optionnel — pilote le bouquet par pas de 1 % */}
              <div>
                <div className="flex items-baseline justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-wider text-navy/50 font-bold">Ajuster en % (optionnel)</div>
                  <div className="text-xs text-navy/60 tabular-nums" data-testid="sim-bouquet-pct-indicator">soit ~{bouquetPctDisplay}&nbsp;% de la valeur retenue</div>
                </div>
                <Slider data-testid="sim-slider-bouquet" min={0} max={90} step={1}
                  value={[sliderValue]} onValueChange={onSliderChange} />
              </div>
            </div>

            <div className="mb-8">
              <div className="flex items-baseline justify-between mb-3">
                <Label className="text-[10px] uppercase tracking-wider text-navy/60 font-bold">Durée</Label>
                <span className="font-serif text-lg text-navy tabular-nums">{duration} ans</span>
              </div>
              <Slider data-testid="sim-slider-duration" min={5} max={30} step={1}
                value={[duration]} onValueChange={(v) => setDuration(v[0])} />
            </div>

            {propertyType === "occupee" && (
              <div className="mb-4 p-4 border border-navy/10 bg-cream">
                <div className="flex items-baseline justify-between mb-3">
                  <Label className="text-[10px] uppercase tracking-wider text-navy/60 font-bold">Durée d&rsquo;occupation estimée</Label>
                  <span className="font-serif text-lg text-navy tabular-nums">{duh} ans</span>
                </div>
                <Slider data-testid="sim-slider-duh" min={0} max={25} step={1} value={[duh]} onValueChange={(v) => setDuh(v[0])} />
                <div className="text-xs text-navy/60 mt-3 tabular-nums">Décote appliquée&nbsp;: <span className="font-semibold text-navy">{(decote * 100).toFixed(1)}&nbsp;%</span> (plafonnée à 40&nbsp;%)</div>
              </div>
            )}
          </Reveal>

          {/* RESULTS */}
          <Reveal delay={100} as="div" className="bg-navy text-cream p-8 md:p-10 flex flex-col">
            <h2 className="font-serif text-2xl mb-8 text-cream">Estimation</h2>

            <div className="space-y-5 mb-8">
              <ResRow label="Valeur retenue" value={retenueValue} note={propertyType === "occupee" ? `après décote de ${(decote * 100).toFixed(1)}\u00a0%` : "valeur vénale"} />
              <ResRow label="Bouquet initial" value={bouquet} highlight />
              <ResRow label="Solde à échelonner" value={solde} />
              <div className="border-t border-cream/15 pt-5">
                <div className="text-xs uppercase tracking-wider text-cream/60 mb-1">Mensualité fixe</div>
                <div data-testid="sim-result-monthly" className="font-serif text-4xl md:text-5xl text-accent-brand font-semibold tabular-nums">
                  <AnimatedNumber value={monthly} format={(n) => eur(Math.round(n))} />
                </div>
                <div className="text-xs text-cream/60 mt-1 tabular-nums">pendant {duration} ans — soit {duration * 12} mensualités</div>
              </div>
            </div>

            <div className="text-xs uppercase tracking-[0.15em] text-accent-brand font-bold border-t border-cream/15 pt-5 mb-3">Prix ferme · Mensualités fixes · Sans intérêts · Sans indexation</div>
            <div className="text-xs text-cream/60 leading-relaxed mb-6">
              Estimation indicative. Le régime fiscal dépend de votre situation ; à valider avec votre notaire.
            </div>

            <div className="mb-6">
              <NotaryFeesBlock price={retenueValue} variant="dark" />
            </div>

            <button
              type="button"
              data-testid="sim-pdf-btn"
              onClick={downloadPdf}
              className="w-full border border-cream/25 text-cream py-3 font-bold tracking-wide hover:bg-cream hover:text-navy transition-colors mb-4"
            >
              Télécharger le récapitulatif (PDF)
            </button>

            <form onSubmit={submit} className="border-t border-cream/15 pt-6 space-y-3 mt-auto" data-testid="lead-form">
              <div className="text-sm font-bold mb-2 text-cream">Être recontacté par un conseiller</div>
              <Input data-testid="lead-name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Nom" className="bg-white text-navy border-transparent" />
              <Input data-testid="lead-email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="bg-white text-navy border-transparent" />
              <Input data-testid="lead-phone" required value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Téléphone" className="bg-white text-navy border-transparent" />
              <button disabled={submitting} data-testid="lead-submit" className="w-full bg-accent-brand text-white py-3 font-bold tracking-wide hover:bg-[#B08D22] transition-colors disabled:opacity-60">
                {submitting ? "Envoi..." : "Être recontacté"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
}

function ResRow({ label, value, note, highlight }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-cream/60 mb-0.5">{label}</div>
      <div className={`font-serif text-2xl md:text-3xl tabular-nums ${highlight ? "text-accent-brand font-semibold" : "text-cream"}`}>
        <AnimatedNumber value={value} format={(n) => eur(Math.round(n))} />
      </div>
      {note && <div className="text-[11px] text-cream/50 mt-0.5 tabular-nums">{note}</div>}
    </div>
  );
}
